import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import RunHistoryPanel from "./RunHistoryPanel";
import { useRegisterBackgroundRun } from "./backgroundRuns";
import { labelForRun } from "./runStore";
import {
  Modal,
  PrimaryButton,
  SecondaryButton,
  Spinner,
  Toast,
} from "./shared";
import { WebAlternative } from "./types";
import {
  CatalogApi,
  ChainProgress,
  extractApiError,
  extractWebAlternatives,
} from "./useCatalogApi";
import { useRunHistory } from "./useRunHistory";

/** What we persist per sourcing-finder run so it can be fully re-opened. */
interface SourcingRunPayload {
  searchNames: string[];
  products: WebAlternative[];
}

/** Stable key for a discovered/manual product (for per-item added status). */
const productKey = (name: string, brand?: string | null): string =>
  `${name}|${brand || ""}`;

// Friendly labels for the web-finder chain's steps (see the builder doc).
const FINDER_STEP_LABELS: Record<string, string> = {
  "node-1": "Reading the product list",
  "node-2": "Web-searching keto/UPF alternatives",
  "node-3": "Combining the results",
};

type Stage = "select" | "running" | "results";

interface SourcingFinderDialogProps {
  open: boolean;
  onClose: () => void;
  /** Re-open the (backgrounded) dialog — used by the pill/completion toast. */
  onReopen: () => void;
  api: CatalogApi;
  chainId: string;
  /** Items from the run that got ZERO suggestions (the primary targets). */
  gapItems: string[];
  /** Items that DID find replacements (optionally include to expand options). */
  matchedItems: string[];
  /** Whether an ingest chain is configured (needed for "Add Products"). */
  ingestConfigured: boolean;
  /** Kick off a background ingest run for the chosen products (Add-products flow). */
  onIngest: (foodInput: string, count: number) => Promise<void> | void;
}

const uniq = (xs: string[]): string[] => {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const x of xs) {
    const t = x.trim();
    if (!t) continue;
    const k = t.toLowerCase();
    if (seen.has(k)) continue;
    seen.add(k);
    out.push(t);
  }
  return out;
};

/**
 * "Close the loop" dialog. For grocery items with no (or weak) replacement
 * suggestions, web-search keto/UPF-friendly alternative *product names* via the
 * finder chain, then push the chosen ones into the normal food-ingest
 * Add-products flow so the catalog gains coverage.
 *
 * State lives here and the parent keeps this mounted (toggling `open`), so a
 * run keeps going and its results survive hiding/reopening the dialog.
 */
const SourcingFinderDialog: React.FC<SourcingFinderDialogProps> = ({
  open,
  onClose,
  onReopen,
  api,
  chainId,
  gapItems,
  matchedItems,
  ingestConfigured,
  onIngest,
}) => {
  const [stage, setStage] = useState<Stage>("select");

  // Stage 1 — which item names to search.
  const [gapSel, setGapSel] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(uniq(gapItems).map((n) => [n, true]))
  );
  const [includeMatched, setIncludeMatched] = useState(false);
  const [matchedSel, setMatchedSel] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(uniq(matchedItems).map((n) => [n, true]))
  );
  const [manualSearchInput, setManualSearchInput] = useState("");
  const [manualSearchItems, setManualSearchItems] = useState<string[]>([]);

  // Run state.
  const [progress, setProgress] = useState<ChainProgress | null>(null);
  const [credits, setCredits] = useState<number | null>(null);
  const [error, setError] = useState("");
  const abortRef = useRef<AbortController | null>(null);

  // Stage 3 — discovered products + what to add.
  const [products, setProducts] = useState<WebAlternative[] | null>(null);
  const [productSel, setProductSel] = useState<Record<number, boolean>>({});
  const [manualProductInput, setManualProductInput] = useState("");
  const [manualProducts, setManualProducts] = useState<string[]>([]);
  const [adding, setAdding] = useState(false);
  const [addErr, setAddErr] = useState("");

  // Persisted run history (localStorage; see runStore.ts). Survives closing the
  // dialog and even a full grocery re-run (this component is keyed per run).
  const history = useRunHistory<SourcingRunPayload>("sourcing");
  // Id of the run currently shown (used to record which products were added).
  const [runId, setRunId] = useState("");
  const currentRun = runId ? history.getRun(runId) : undefined;
  const addedStatus = currentRun?.itemStatus || {};

  const gapNames = useMemo(() => uniq(gapItems), [gapItems]);
  const matchedNames = useMemo(() => uniq(matchedItems), [matchedItems]);

  const searchNames = useMemo(() => {
    const chosen = [
      ...gapNames.filter((n) => gapSel[n]),
      ...(includeMatched ? matchedNames.filter((n) => matchedSel[n]) : []),
      ...manualSearchItems,
    ];
    return uniq(chosen);
  }, [
    gapNames,
    matchedNames,
    gapSel,
    matchedSel,
    includeMatched,
    manualSearchItems,
  ]);

  const addManualSearch = () => {
    const v = manualSearchInput.trim();
    if (!v) return;
    setManualSearchItems((xs) => uniq([...xs, v]));
    setManualSearchInput("");
  };

  const addManualProduct = () => {
    const v = manualProductInput.trim();
    if (!v) return;
    setManualProducts((xs) => uniq([...xs, v]));
    setManualProductInput("");
  };

  const runFinder = async () => {
    setError("");
    if (!chainId) {
      setError(
        "The web product finder isn't configured yet. Please contact Passio support."
      );
      return;
    }
    if (searchNames.length === 0) {
      setError("Pick at least one item to search for.");
      return;
    }
    const controller = new AbortController();
    abortRef.current = controller;
    setStage("running");
    setProgress(null);
    setCredits(null);
    setProducts(null);
    try {
      const resp = await api.runChain(
        chainId,
        { products_input: searchNames.join("\n") },
        {
          signal: controller.signal,
          onStatus: (_s, c) => {
            if (typeof c === "number") setCredits(c);
          },
          onProgress: setProgress,
        }
      );
      const found = extractWebAlternatives(resp);
      setProducts(found);
      const sel: Record<number, boolean> = {};
      found.forEach((_, i) => (sel[i] = true));
      setProductSel(sel);
      setStage("results");
      const rid = resp.id || `finder-${Date.now()}`;
      setRunId(rid);
      history.saveRun({
        id: rid,
        kind: "sourcing",
        status: "completed",
        label: labelForRun(searchNames.join("\n"), searchNames.length),
        createdAt: Date.now(),
        updatedAt: Date.now(),
        inputSummary: `${searchNames.length} searched`,
        chainRunId: resp.id || undefined,
        payload: { searchNames, products: found },
        counts: { found: found.length },
      });
    } catch (err) {
      if ((err as Error)?.name === "AbortError") {
        setError("Cancelled.");
      } else {
        setError(extractApiError(err, "Web finder run failed"));
      }
      setStage("select");
    } finally {
      abortRef.current = null;
    }
  };

  const cancel = useCallback(() => abortRef.current?.abort(), []);

  // Reload a previous finder run's discovered products (survives dialog close /
  // a fresh grocery run). Already-added products are badged from itemStatus.
  const reloadRun = (run: { id: string; payload?: SourcingRunPayload }) => {
    if (!run.payload) return;
    const found = run.payload.products || [];
    setProducts(found);
    const sel: Record<number, boolean> = {};
    found.forEach((_, i) => (sel[i] = true));
    setProductSel(sel);
    setManualProducts([]);
    setRunId(run.id);
    setError("");
    setAddErr("");
    setStage("results");
  };

  const chosenProducts = useMemo(() => {
    const fromChain = (products || [])
      .filter((_, i) => productSel[i])
      .map((p) => (p.brand ? `${p.name}, ${p.brand}` : p.name));
    return uniq([...fromChain, ...manualProducts]);
  }, [products, productSel, manualProducts]);

  const addProducts = async () => {
    setAddErr("");
    if (!ingestConfigured) {
      setAddErr("No ingest chain id configured — set it in ⚙ Settings.");
      return;
    }
    if (chosenProducts.length === 0) {
      setAddErr("Select at least one product to add.");
      return;
    }
    setAdding(true);
    try {
      await onIngest(chosenProducts.join("\n"), chosenProducts.length);
      // Record which products were pushed to ingest so the persisted run knows
      // they were handled (survives reopening / reloading this run later).
      if (runId) {
        (products || []).forEach((p, i) => {
          if (productSel[i])
            history.setItemStatus(runId, productKey(p.name, p.brand), {
              state: "added",
            });
        });
        manualProducts.forEach((m) =>
          history.setItemStatus(runId, `manual:${m}`, { state: "added" })
        );
        const prev = history.getRun(runId);
        history.patchRun(runId, {
          counts: {
            ...(prev?.counts || {}),
            found: products?.length ?? prev?.counts?.found ?? 0,
            added: chosenProducts.length,
          },
        });
      }
      // The ingest job's live dialog (managed by the Catalog Manager) takes over
      // from here; close this dialog so it's visible.
      onClose();
    } catch (err) {
      if ((err as Error)?.name !== "RunCancelledError") {
        setAddErr(extractApiError(err, "Failed to start research"));
      }
    } finally {
      setAdding(false);
    }
  };

  const running = stage === "running";

  // Surface this run in the shared background-runs tray (bottom-right) while it
  // runs, instead of a bespoke floating pill. "View" reopens this dialog.
  useRegisterBackgroundRun(running, {
    id: "sourcing-finder",
    title: "Finding product options",
    detail: progress?.currentNodeId
      ? FINDER_STEP_LABELS[progress.currentNodeId] || "Working…"
      : "Searching…",
    onView: onReopen,
    onCancel: cancel,
  });

  // Notify (toast) when a backgrounded run finishes while the dialog is hidden,
  // so the user knows there's a "pick & add" step waiting.
  const [doneToastOpen, setDoneToastOpen] = useState(false);
  const prevStageRef = useRef<Stage>(stage);
  useEffect(() => {
    const was = prevStageRef.current;
    prevStageRef.current = stage;
    if (was !== "results" && stage === "results" && !open) {
      setDoneToastOpen(true);
    }
  }, [stage, open]);
  useEffect(() => {
    if (open) setDoneToastOpen(false);
  }, [open]);

  const foundCount = products?.length ?? 0;

  const footer =
    stage === "results" ? (
      <>
        <SecondaryButton type="button" onClick={() => setStage("select")}>
          ← Search again
        </SecondaryButton>
        <PrimaryButton
          type="button"
          onClick={addProducts}
          disabled={adding || chosenProducts.length === 0}
        >
          {adding
            ? "Starting…"
            : `Add ${chosenProducts.length} product${
                chosenProducts.length === 1 ? "" : "s"
              } →`}
        </PrimaryButton>
      </>
    ) : stage === "select" ? (
      <>
        <SecondaryButton type="button" onClick={onClose}>
          Close
        </SecondaryButton>
        <PrimaryButton
          type="button"
          onClick={runFinder}
          disabled={searchNames.length === 0}
        >
          Find options ({searchNames.length}) →
        </PrimaryButton>
      </>
    ) : (
      <>
        <SecondaryButton type="button" onClick={onClose}>
          ▾ Run in background
        </SecondaryButton>
        <SecondaryButton type="button" onClick={cancel}>
          Cancel
        </SecondaryButton>
      </>
    );

  return (
    <>
      <Modal
        open={open}
        onClose={onClose}
        dismissable
        title="Find & add options for sourcing gaps"
        footer={footer}
      >
        {stage === "select" && (
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Web-search keto / low-UPF alternative products (Dr. Berg rules)
              for the items below, then push the ones you like into{" "}
              <span className="font-semibold">Add products</span> to research
              and add them to the catalog.
            </p>

            {gapNames.length > 0 ? (
              <div>
                <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-amber-700">
                  Items with no suggestions ({gapNames.length})
                </div>
                <div className="space-y-1 rounded-md border border-amber-200 bg-amber-50 p-2.5">
                  {gapNames.map((n) => (
                    <label
                      key={n}
                      className="flex cursor-pointer items-center gap-2 text-sm text-gray-800"
                    >
                      <input
                        type="checkbox"
                        checked={!!gapSel[n]}
                        onChange={(e) =>
                          setGapSel((s) => ({ ...s, [n]: e.target.checked }))
                        }
                        className="h-4 w-4 cursor-pointer"
                      />
                      {n}
                    </label>
                  ))}
                </div>
              </div>
            ) : (
              <div className="rounded-md border border-dashed border-gray-300 bg-gray-50 p-2.5 text-sm text-gray-500">
                This run had no items without suggestions. You can still add
                items manually below, or include items that found replacements.
              </div>
            )}

            {matchedNames.length > 0 && (
              <div>
                <label className="flex cursor-pointer items-start gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={includeMatched}
                    onChange={(e) => setIncludeMatched(e.target.checked)}
                    className="mt-0.5 h-4 w-4 cursor-pointer"
                  />
                  <span>
                    Also search items that{" "}
                    <span className="font-semibold">did</span> find replacements
                    ({matchedNames.length}) — to expand their options
                  </span>
                </label>
                {includeMatched && (
                  <div className="mt-2 space-y-1 rounded-md border border-gray-200 bg-white p-2.5">
                    {matchedNames.map((n) => (
                      <label
                        key={n}
                        className="flex cursor-pointer items-center gap-2 text-sm text-gray-800"
                      >
                        <input
                          type="checkbox"
                          checked={!!matchedSel[n]}
                          onChange={(e) =>
                            setMatchedSel((s) => ({
                              ...s,
                              [n]: e.target.checked,
                            }))
                          }
                          className="h-4 w-4 cursor-pointer"
                        />
                        {n}
                      </label>
                    ))}
                  </div>
                )}
              </div>
            )}

            <div>
              <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-400">
                Add another item to search
              </div>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={manualSearchInput}
                  onChange={(e) => setManualSearchInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      addManualSearch();
                    }
                  }}
                  placeholder="e.g. keto tortillas"
                  className="flex-1 rounded-md border border-gray-300 px-2.5 py-1.5 text-sm focus:border-indigo-500 focus:outline-none"
                />
                <SecondaryButton type="button" onClick={addManualSearch}>
                  Add
                </SecondaryButton>
              </div>
              {manualSearchItems.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {manualSearchItems.map((n) => (
                    <span
                      key={n}
                      className="inline-flex items-center gap-1 rounded-full bg-indigo-100 px-2.5 py-1 text-xs font-medium text-indigo-800"
                    >
                      {n}
                      <button
                        type="button"
                        onClick={() =>
                          setManualSearchItems((xs) =>
                            xs.filter((x) => x !== n)
                          )
                        }
                        className="text-indigo-500 hover:text-indigo-800"
                        aria-label={`Remove ${n}`}
                      >
                        ✕
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            {!chainId && (
              <p className="text-sm text-amber-700">
                No web product finder chain id configured — set it in ⚙
                Settings.
              </p>
            )}
            {error && <p className="text-sm text-red-600">{error}</p>}

            {/* Reload a previous search so you don't lose results on close. */}
            <RunHistoryPanel
              runs={history.runs}
              onReload={reloadRun}
              onDelete={history.removeRun}
              onClearAll={history.clearAll}
              title="Recent searches"
              subtitle="Reload the products from a previous search — saved in this browser. ‘Added’ counts show which ones you already pushed to Add products."
              renderMeta={(run) => {
                const c = run.counts || {};
                const bits: string[] = [];
                if (typeof c.found === "number") bits.push(`${c.found} found`);
                if (c.added) bits.push(`${c.added} added`);
                return (
                  <>
                    {run.status === "completed" ? "Ready" : run.status}
                    {bits.length > 0 ? ` · ${bits.join(" · ")}` : ""}
                  </>
                );
              }}
            />
          </div>
        )}

        {stage === "running" && (
          <div className="space-y-3 py-4">
            <Spinner label="Web-searching keto/UPF alternatives…" />
            {(() => {
              const cur = progress?.nodes.find(
                (n) => n.id === progress?.currentNodeId
              );
              const label = progress?.currentNodeId
                ? FINDER_STEP_LABELS[progress.currentNodeId] ||
                  `Step ${
                    (progress.nodes || []).findIndex(
                      (n) => n.id === progress.currentNodeId
                    ) + 1
                  }`
                : "";
              if (!label) return null;
              return (
                <p className="text-sm text-gray-600">
                  {label}
                  {cur?.progress ? ` · ${cur.progress}` : ""}
                </p>
              );
            })()}
            {typeof credits === "number" && (
              <p className="text-xs text-gray-400">
                Charged {credits.toLocaleString()} tokens
              </p>
            )}
            <p className="text-xs text-gray-400">
              You can close this dialog — the search keeps running and results
              will be here when you reopen it.
            </p>
          </div>
        )}

        {stage === "results" && (
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              {products && products.length > 0
                ? "Pick the products to add. Selected items are researched and added to the catalog just like the Add products tab."
                : "The web search didn't surface compliant products. Add items manually below, or search again with different terms."}
            </p>

            {products && products.length > 0 && (
              <div className="space-y-1.5">
                {products.map((p, i) => (
                  <label
                    key={`${p.name}|${p.brand || ""}|${i}`}
                    className="flex cursor-pointer items-start gap-2 rounded-md border border-gray-200 bg-white p-2.5 text-sm hover:bg-gray-50"
                  >
                    <input
                      type="checkbox"
                      checked={!!productSel[i]}
                      onChange={(e) =>
                        setProductSel((s) => ({ ...s, [i]: e.target.checked }))
                      }
                      className="mt-0.5 h-4 w-4 cursor-pointer"
                    />
                    <span className="min-w-0">
                      <span className="font-semibold text-gray-900">
                        {p.name}
                      </span>
                      {p.brand && (
                        <span className="text-gray-500"> · {p.brand}</span>
                      )}
                      {addedStatus[productKey(p.name, p.brand)] && (
                        <span className="ml-1.5 inline-flex items-center rounded-full bg-green-100 px-1.5 py-0.5 text-[10px] font-semibold text-green-700">
                          ✓ added
                        </span>
                      )}
                      {p.for_item && (
                        <span className="block text-xs text-gray-400">
                          for “{p.for_item}”
                        </span>
                      )}
                    </span>
                  </label>
                ))}
              </div>
            )}

            <div>
              <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-400">
                Add a product manually
              </div>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={manualProductInput}
                  onChange={(e) => setManualProductInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      addManualProduct();
                    }
                  }}
                  placeholder="e.g. Whisps Cheese Crisps"
                  className="flex-1 rounded-md border border-gray-300 px-2.5 py-1.5 text-sm focus:border-indigo-500 focus:outline-none"
                />
                <SecondaryButton type="button" onClick={addManualProduct}>
                  Add
                </SecondaryButton>
              </div>
              {manualProducts.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {manualProducts.map((n) => (
                    <span
                      key={n}
                      className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2.5 py-1 text-xs font-medium text-green-800"
                    >
                      {n}
                      <button
                        type="button"
                        onClick={() =>
                          setManualProducts((xs) => xs.filter((x) => x !== n))
                        }
                        className="text-green-600 hover:text-green-900"
                        aria-label={`Remove ${n}`}
                      >
                        ✕
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            {!ingestConfigured && (
              <p className="text-sm text-amber-700">
                No ingest chain id configured — set it in ⚙ Settings to add
                products.
              </p>
            )}
            {addErr && <p className="text-sm text-red-600">{addErr}</p>}
          </div>
        )}
      </Modal>

      {/* A backgrounded run stays visible in the shared background-runs tray
          (bottom-right) — no separate floating pill. */}

      {/* Completion nudge when the run finished while hidden. */}
      <Toast
        open={!open && doneToastOpen}
        tone={foundCount > 0 ? "success" : "info"}
        message={
          foundCount > 0
            ? `Found ${foundCount} option${
                foundCount === 1 ? "" : "s"
              } for your sourcing gaps — open to pick and add them.`
            : "The web search didn't surface compliant options — open to add items manually or search again."
        }
        actionLabel="Open"
        onAction={onReopen}
        onClose={() => setDoneToastOpen(false)}
        duration={0}
      />
    </>
  );
};

export default SourcingFinderDialog;
